import React, { useMemo, useState } from "react";
import { sectionTemplates } from "../../../../data/database";

const isBlank = (v) => (v ?? "").toString().trim().length === 0;

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const getAlignValue = (style) => {
  const v = String(style?.textAlign || style?.alignment || "").toLowerCase().trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return "center";
};

const getAlignClass = (style) => {
  const a = getAlignValue(style);
  if (a === "left") return "text-left";
  if (a === "right") return "text-right";
  return "text-center";
};

const GallerySection1 = ({ config, style }) => {
  const [selectedProject, setSelectedProject] = useState(null);

  const tpl = sectionTemplates?.gallery?.template1 || {};
  const cfg = { ...(tpl.config || {}), ...(config || {}) };
  const s = { ...(tpl.style || {}), ...(style || {}) };

  const projects = useMemo(() => (Array.isArray(cfg?.projects) ? cfg.projects : []), [cfg]);

  const openModal = (project) => setSelectedProject(project);
  const closeModal = () => setSelectedProject(null);

  const bg = buildBg(s, "bg-gradient-to-b from-white to-gray-50");
  const alignClass = getAlignClass(s);

  const accent = (s?.primaryBtnBg || "").trim();
  const hasAccent = accent.length > 0;

  const cardButtonText = cfg?.cardButtonText ?? "View Project Details";
  const modalPrimaryText = cfg?.modalPrimaryButtonText ?? "View Live Project";

  const primaryBtnStyle = {
    backgroundColor: s?.primaryBtnBg || undefined,
    color: s?.primaryBtnText || undefined,
  };

  const fallbackStats = [
    { value: `${projects.length}+`, label: "Completed Projects" },
    { value: "50+", label: "Happy Clients" },
    { value: "24/7", label: "Support" },
    { value: "98%", label: "Client Retention" },
  ];
  const stats = Array.isArray(cfg?.stats) && cfg.stats.length ? cfg.stats : fallbackStats;

  return (
    <section
      dir={cfg?.direction || "ltr"}
      style={{ ...bg.style, ...(hasAccent ? { "--accent": accent } : undefined) }}
      className={`py-24 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <div className={`${alignClass} mb-16`}>
          <h2
            style={{ color: s?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            {cfg?.title || "Our Work"}
          </h2>
          <p
            style={{ color: s?.subtitleColor || undefined }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {cfg?.subtitle || "A selection of projects we are proud of."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => (
            <div
              key={project.id}
              style={{
                backgroundColor: s?.galleryCardBg || undefined,
                borderColor: s?.galleryCardBorder || undefined,
              }}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer border"
              onClick={() => openModal(project)}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div
                  style={{
                    background: s?.galleryOverlayColor
                      ? `linear-gradient(to top, ${s.galleryOverlayColor}, transparent)`
                      : undefined,
                  }}
                  className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                />
              </div>

              <div className={`p-6 ${alignClass}`}>
                <h3
                  style={{ color: s?.galleryCardTitleColor || undefined }}
                  className={`text-xl font-bold text-gray-900 mb-3 transition-colors ${
                    hasAccent ? "group-hover:text-[var(--accent)]" : "group-hover:text-blue-600"
                  }`}
                >
                  {project.title}
                </h3>

                <p
                  style={{ color: s?.galleryCardDescriptionColor || undefined }}
                  className="text-gray-600 text-sm line-clamp-2"
                >
                  {project.description}
                </p>

                {!isBlank(cardButtonText) && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      className={`font-medium text-sm inline-flex items-center group ${
                        hasAccent ? "text-[var(--accent)]" : "text-blue-600"
                      }`}
                    >
                      {cardButtonText}
                      <span className="ml-2 rtl:ml-0 rtl:mr-2 transform group-hover:translate-x-2 rtl:group-hover:-translate-x-2 transition-transform">
                        →
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((st, idx) => (
            <div
              key={idx}
              style={{
                backgroundColor: s?.galleryStatsCardBg || undefined,
                borderColor: s?.galleryStatsCardBorder || undefined,
              }}
              className={`bg-white p-6 rounded-xl shadow-md border ${alignClass}`}
            >
              <div
                style={{
                  color: s?.galleryStatsValueColor || undefined,
                }}
                className={`text-3xl font-bold mb-2 ${hasAccent ? "text-[var(--accent)]" : "text-blue-600"}`}
              >
                {st.value}
              </div>
              <div
                style={{ color: s?.galleryStatsLabelColor || undefined }}
                className="text-gray-600"
              >
                {st.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div
            style={{ backgroundColor: s?.galleryModalBg || undefined }}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            <div className={`p-6 border-b border-gray-200 flex justify-between items-center ${alignClass}`}>
              <div>
                <h3
                  style={{ color: s?.galleryModalTitleColor || undefined }}
                  className="text-2xl font-bold text-gray-900"
                >
                  {selectedProject.title}
                </h3>
                <div
                  style={{ color: s?.galleryModalCategoryColor || undefined }}
                  className={`font-medium mt-1 ${hasAccent ? "text-[var(--accent)]" : "text-blue-600"}`}
                >
                  {selectedProject.category}
                </div>
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-2xl">
                ×
              </button>
            </div>

            <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(90vh - 120px)" }}>
              <div className="mb-6">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-64 md:h-80 object-cover rounded-xl"
                />
              </div>

              <div className={`${alignClass} prose max-w-none`}>
                <h4
                  style={{ color: s?.galleryModalTitleColor || undefined }}
                  className="text-xl font-bold text-gray-900 mb-4"
                >
                  {cfg?.modalOverviewTitle || "Project Overview"}
                </h4>

                <p
                  style={{ color: s?.galleryModalTextColor || undefined }}
                  className="text-gray-600 mb-6"
                >
                  {selectedProject.description}
                </p>

                <div
                  style={{
                    backgroundColor: s?.galleryModalFeatureBoxBg || undefined,
                    borderColor: s?.galleryModalFeatureBoxBorder || undefined,
                  }}
                  className="bg-gray-50 rounded-xl p-6 border"
                >
                  <h5
                    style={{ color: s?.galleryModalFeatureTitleColor || undefined }}
                    className="font-bold text-gray-900 mb-4"
                  >
                    {cfg?.modalFeaturesTitle || "Key Features"}
                  </h5>
                  <ul className="space-y-2">
                    {(Array.isArray(cfg?.modalFeatures) && cfg.modalFeatures.length
                      ? cfg.modalFeatures
                      : [
                          "Responsive design for all devices",
                          "Performance optimization",
                          "Search engine friendly structure",
                          "Secure data handling",
                        ]
                    ).map((txt, i) => (
                      <li
                        key={i}
                        style={{ color: s?.galleryModalFeatureTextColor || undefined }}
                        className="flex items-center text-gray-700"
                      >
                        <span className="text-green-500 mr-3 rtl:mr-0 rtl:ml-3">✓</span>
                        <span>{txt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end space-x-4 rtl:space-x-reverse">
                <button
                  onClick={closeModal}
                  style={{
                    backgroundColor: s?.gallerySecondaryBtnBg || undefined,
                    borderColor: s?.gallerySecondaryBtnBorder || undefined,
                    color: s?.gallerySecondaryBtnText || undefined,
                  }}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>

                {!isBlank(modalPrimaryText) && (
                  <button
                    style={primaryBtnStyle}
                    className={`px-5 py-2.5 font-medium rounded-lg ${
                      s?.primaryBtnBg ? "" : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                    onClick={() => {
                      const url = selectedProject?.url;
                      if (url) window.open(url, "_blank", "noopener,noreferrer");
                    }}
                  >
                    {modalPrimaryText}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection1;